import { useState, useEffect } from "react";
import appTheme from "src/themes";
import { roundNumber, trimZeros } from "src/utils/genericFunctions";
import { Stack, Typography } from "@mui/material";
import { SxProps, Theme, styled, alpha } from "@mui/material/styles";
import {
  getNextColumn,
  getNextIndex
} from "src/pages/ProduceMovementAndRecord/components/Table/utils";

const StyledInput = styled("input")(({ theme }) => ({
  height: "inherit",
  color: alpha(appTheme.palette.utility.black, 0.87),
  backgroundColor: "inherit",
  width: "100%",
  padding: "4px",
  outline: "none",
  fontFamily: "Roboto",
  fontSize: "14px",
  lineHeight: "1.43em",
  letterSpacing: "0.5px",
  textAlign: "right",
  boxSizing: "border-box",
  borderRadius: "4px",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: alpha(appTheme.palette.utility.black, 0.23),
  transition:
    "border-color 150ms ease-out, border-width 150ms ease-out, box-shadow 150ms ease-out",
  "&:hover:not(:disabled):not(:focus)": {
    borderColor: alpha(appTheme.palette.utility.black, 0.87)
  },
  "&:focus": {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
  }
}));

const InputPMR = ({
  itemId,
  rowData,
  editMode,
  id,
  disabled,
  onBlur,
  sxConfig = {},
  maxLength = 8,
  disableDecimalDigit = false
}: {
  itemId: string | number;
  rowData: any;
  editMode: boolean;
  id: string;
  onBlur?: (item: string, id: string | number, value: any) => any;
  disableDecimalDigit?: boolean;
  disabled?: boolean;
  maxLength?: number;
  sxConfig?: SxProps<Theme>;
}) => {
  const [value, setValue] = useState<string | number>("");

  useEffect(() => {
    const value = rowData[id] === null ? null : roundNumber(rowData[id]);
    if (value === null) {
      setValue("");
    } else {
      setValue(Number(value));
    }
  }, [id, rowData]);

  const handleBlur = (receivedValue: string) => {
    if (receivedValue === "") {
      setValue("");
    }
    onBlur(id, itemId, value);
  };

  /* istanbul ignore next */
  const handleChange = (newValue: string) => {
    if (newValue === "") {
      setValue("");
      return;
    } else if (newValue === "0") {
      setValue(0);
      return;
    }

    let value = trimZeros(newValue);
    const [before, after] = value.split(".");

    if (disableDecimalDigit) value = before;
    if (after?.length > 2) return;
    if (value.includes(".") && before === "") {
      value = "0" + value;
    }

    if (value === "") {
      setValue("");
      return;
    }
    if (Number(value) >= 0 && Number(value) <= 9999999) {
      setValue(value);
    }
  };

  const onKeyDown = (event: any) => {
    if (event.key === "Tab") {
      event.preventDefault();
      const nextId = getNextIndex(rowData?.vertIndex, id);
      const nextInput = document.getElementById(nextId);
      if (nextInput) {
        nextInput.scrollIntoView({ behavior: "smooth" });
        (nextInput as HTMLInputElement).focus();
      } else {
        const nextColumnId = getNextColumn(id, rowData?.vertIndex);
        const nextColumnInput = document.getElementById(nextColumnId);
        if (nextColumnInput) {
          nextColumnInput.scrollIntoView({ behavior: "smooth" });
          (nextColumnInput as HTMLInputElement).focus();
        } else {
          const nextColumnInput = document.getElementById("0-pack");
          nextColumnInput.scrollIntoView({ behavior: "smooth" });
          (nextColumnInput as HTMLInputElement).focus();
        }
      }
    }
  };

  if (editMode) {
    return (
      <Stack
        id="input-with-text-container"
        sx={{
          width: "100%",
          ...sxConfig
        }}
      >
        <StyledInput
          data-testid="InputItemLabel"
          disabled={disabled}
          aria-label="input"
          maxLength={maxLength}
          value={value}
          onChange={(event: any) => handleChange(event.target.value)}
          onBlur={(event: any) => handleBlur(event.target.value)}
          onFocus={(event: any) => event.target.select()}
          onKeyDown={onKeyDown}
          id={`${rowData?.vertIndex}-${id}`}
        />
      </Stack>
    );
  }

  return (
    <Typography data-testid="display-mode" fontSize={14}>
      {rowData[id]}
    </Typography>
  );
};

export default InputPMR;
